import { useEffect, useState } from "react";
import Button from "shared/Button";
import Alert from "shared/Alert";
import Modal from "shared/Modal";
import StatusPill from "shared/StatusPill";
import StatusHelp from "./StatusHelp";
import { showSuccess } from "shared/toast";
import { FORM_CONTROL } from "shared/tableStyles";
import { cn } from "lib/utils";
import { apiErrorMessage } from "../apiError";
import { getPrimeCompany, type PrimeContactWithAppearanceCount } from "../api/CrmApi";
import {
  postConversationMessage, postConversationOutcome, postConversationRedirect,
  type Channel, type Conversation, type ConversationOutcome,
} from "../api/OutreachTouchesApi";
import { announceDueChange } from "../modal/useDueCounts";
import AddPrimeContactModal from "./AddPrimeContactModal";
import ReceivedAtField, { receivedAtIso } from "./ReceivedAtField";

export const OUTCOME_LABEL: Record<ConversationOutcome, string> = {
  interested: "Interested", interested_other_rfp: "Interested, not this RFP", not_interested: "Not interested in this RFP",
  not_interested_all: "Not interested at all (company-wide)", teaming: "Teaming", no_response: "No response", removed: "Asked to be removed", redirected: "Redirected",
};
/** The results a person can choose, in the order of the list. "Redirected" has its own button and "Asked to be removed" its own action;
 *  both (and no other value) still display on a conversation that has them. */
export const CHOOSABLE: ConversationOutcome[] = ["interested", "interested_other_rfp", "not_interested", "not_interested_all", "teaming", "no_response"];

/** An "Interested" that is still open after the RFP's proposal deadline: a hint to update the result, never an automatic change. */
export const staleInterest = (c: Pick<Conversation, "outcome" | "state" | "rfp_deadline_passed">) =>
  c.outcome === "interested" && c.state !== "closed" && !!c.rfp_deadline_passed;
export const STALE_INTEREST_HINT = "The proposal deadline has passed. Set Teaming, Interested not this RFP, Not interested or No response.";

/** What each result does, said in the dialog before it is saved. `{who}` and `{company}` are filled in. */
const RESULT_HELP: Record<string, { lead: string; effect: string }> = {
  interested: { lead: "{who} wants to work with us on this RFP. The conversation stays open.", effect: "{company} counts as a warm company: nobody there gets a cold email or LinkedIn message on a later RFP. Someone writes to them instead." },
  interested_other_rfp: { lead: "{who} wants to work with us, but this RFP is not a fit. The conversation closes.", effect: "{company} counts as a warm company: nobody there gets a cold email or LinkedIn message on a later RFP. Someone writes to them instead." },
  not_interested: { lead: "{who} is not interested in this RFP. The conversation closes.", effect: "This is about this RFP only. {company} can still be contacted on a later RFP." },
  not_interested_all: { lead: "{who} is not interested in working with us at all. The conversation closes.", effect: "{company} will not get a cold email or LinkedIn message on any later RFP, for anyone there, until someone changes this result." },
  teaming: { lead: "{who} and {company} are teaming up with us. The conversation closes.", effect: "{company} will not be contacted in any future campaign: nobody there gets a cold email or LinkedIn message. You can lift this by setting the conversation back to Open." },
  no_response: { lead: "{who} has not answered. The conversation closes.", effect: "Nothing changes for later RFPs: {company} can still be contacted. A new message from them reopens it." },
};
const fill = (text: string, who: string, company: string | null) => text.replace("{who}", who).replace(/\{company\}/g, company ?? "their company");

interface Props {
  conversation: Conversation;
  /** Called after a change has been saved, with the fresh conversation when the server sent one (a redirect sends the NEW one). */
  onChanged: (updated?: Conversation, kind?: "redirect") => void;
}

/** What a person can do to a conversation: log a message, set the result (Open, Interested, Not interested, Teaming), reopen, redirect it to a
 *  colleague, or record that they asked to be removed. Used in the Inbox thread. */
export default function ConversationActions({ conversation: c, onChanged }: Props) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<null | { kind: "result"; outcome: ConversationOutcome } | { kind: "remove" } | { kind: "redirect" }>(null);

  /** Run one change, tell the page, show a failure inline. */
  async function change<T extends Conversation | unknown>(fn: () => Promise<T>, done?: string, kind?: "redirect"): Promise<boolean> {
    setBusy(true);
    setError("");
    try {
      const result = await fn();
      if (done) showSuccess(done);
      announceDueChange();
      onChanged((result as Conversation)?.messages ? (result as Conversation) : undefined, kind);
      return true;
    } catch (e) {
      setError(apiErrorMessage(e, "Could not save that. Reload and try again."));
      return false;
    } finally {
      setBusy(false);
    }
  }

  function pickResult(value: string) {
    if (!value) { void change(() => postConversationOutcome(c.id, { outcome: null }), "Reopened"); return; }
    setDialog({ kind: "result", outcome: value as ConversationOutcome });       // every result asks for its optional note first
  }

  const who = c.contact_name || c.contact_email || "Unknown contact";
  const legacy = c.outcome && !CHOOSABLE.includes(c.outcome) ? c.outcome : null;       // "redirected" or "asked to be removed", which have their own actions
  return (
    <div className="space-y-3">
      {error && <Alert tone="danger" role="alert">{error}</Alert>}
      <p className="text-xs text-muted-foreground" data-testid="result-line">
        Result: <strong className="text-foreground">{c.outcome ? OUTCOME_LABEL[c.outcome] : "Open"}</strong>{c.outcome_note ? ` · ${c.outcome_note}` : ""}
        {c.state === "closed" ? ". Closed: a new message from them reopens it." : ""}
        {staleInterest(c) && <> <StatusPill tone="warning" size="sm" title={STALE_INTEREST_HINT}>RFP closed, update the result</StatusPill></>}
      </p>
      {c.state !== "closed" && <Composer conversation={c} busy={busy} change={change} draft={c.suggested_draft ?? null} />}

      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor={`outcome-${c.id}`} className="text-xs font-medium text-muted-foreground">Where it stands</label>
        <StatusHelp />
        <select id={`outcome-${c.id}`} value={c.outcome ?? ""} disabled={busy || legacy === "redirected"} onChange={(e) => pickResult(e.target.value)} className={`px-3 py-1.5 ${FORM_CONTROL}`}>
          <option value="">Open</option>
          {legacy && <option value={legacy} disabled>{OUTCOME_LABEL[legacy]}</option>}
          {CHOOSABLE.map((o) => <option key={o} value={o}>{OUTCOME_LABEL[o]}</option>)}
        </select>
        {c.state === "closed" && (
          <Button variant="outline" size="sm" disabled={busy} onClick={() => void change(() => postConversationOutcome(c.id, { outcome: null }), "Reopened")}>Reopen</Button>
        )}
        {c.state !== "closed" && c.no_answer && <Button variant="outline" size="sm" disabled={busy} onClick={() => setDialog({ kind: "result", outcome: "no_response" })}>Close as no response</Button>}
        {c.state !== "closed" && <Button variant="outline" size="sm" disabled={busy} onClick={() => setDialog({ kind: "redirect" })}>Redirect to a colleague</Button>}
        <Button variant="danger" size="sm" disabled={busy} onClick={() => setDialog({ kind: "remove" })}>They asked to be removed</Button>
      </div>

      {dialog?.kind === "result" && (
        <ResultDialog
          outcome={dialog.outcome} who={who} company={c.company_name} busy={busy} initialNote={c.outcome === dialog.outcome ? c.outcome_note ?? "" : ""}
          onCancel={() => setDialog(null)}
          onConfirm={async (note) => { const ok = await change(() => postConversationOutcome(c.id, { outcome: dialog.outcome, ...(note ? { note } : {}) }), `Saved: ${OUTCOME_LABEL[dialog.outcome]}`); if (ok) setDialog(null); }}
        />
      )}
      {dialog?.kind === "remove" && (
        <RemoveDialog
          who={who} busy={busy} onCancel={() => setDialog(null)}
          onConfirm={async (reason) => {
            const ok = await change(() => postConversationOutcome(c.id, { outcome: "removed", removed: true, note: reason }), "Closed and marked Do Not Contact");
            if (ok) setDialog(null);
          }}
        />
      )}
      {dialog?.kind === "redirect" && (
        <RedirectDialog
          conversation={c} busy={busy} onCancel={() => setDialog(null)}
          onPick={async (id) => { const ok = await change(() => postConversationRedirect(c.id, id), "Started a conversation with the new contact", "redirect"); if (ok) setDialog(null); }}
        />
      )}
    </div>
  );
}

function Composer({ conversation, busy, change, draft }: { conversation: Conversation; busy: boolean; change: <T>(fn: () => Promise<T>, done?: string) => Promise<boolean>; draft: { text: string; channel: Channel } | null }) {
  const [sender, setSender] = useState<"contact" | "us">(draft ? "us" : "contact");
  const [channel, setChannel] = useState<Channel>(draft?.channel ?? "linkedin");
  const [text, setText] = useState(draft?.text ?? "");
  const [drafted, setDrafted] = useState(draft !== null);       // the text is still the suggestion, not yet edited
  const [receivedAt, setReceivedAt] = useState("");
  async function add() {
    if (!text.trim()) return;
    const at = sender === "contact" ? receivedAtIso(receivedAt) : undefined;
    const ok = await change(() => postConversationMessage(conversation.id, { sender, channel, text: text.trim(), ...(at ? { received_at: at } : {}) }), "Message added");
    if (ok) { setText(""); setReceivedAt(""); setDrafted(false); }
  }
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2" role="group" aria-label="Who wrote it">
        {([["contact", "They replied"], ["us", "We replied"]] as const).map(([v, label]) => (
          <button key={v} type="button" aria-pressed={sender === v} onClick={() => setSender(v)}
            className={cn("px-3 py-1.5 rounded-lg border text-xs font-medium", sender === v ? "border-primary text-primary bg-primary-soft" : "border-border text-muted-foreground bg-surface")}>
            {label}
          </button>
        ))}
        <select aria-label="Channel" value={channel} onChange={(e) => setChannel(e.target.value as Channel)} className={`px-3 py-1.5 ${FORM_CONTROL}`}>
          <option value="linkedin">LinkedIn</option>
          <option value="email">Email</option>
        </select>
      </div>
      {drafted && <p className="text-xs font-medium text-warning" data-testid="draft-label">Suggested draft, edit before sending</p>}
      <textarea aria-label="Message text" value={text} onChange={(e) => { setText(e.target.value); setDrafted(false); }} rows={3} maxLength={20000}
        placeholder={sender === "contact" ? "Paste what they wrote" : "Paste what we sent"} className={`w-full px-3 py-2 ${FORM_CONTROL}`} />
      {sender === "contact" && <ReceivedAtField id={`received-${conversation.id}`} value={receivedAt} onChange={setReceivedAt} disabled={busy} />}
      {sender === "contact" && <p className="text-xs text-muted-foreground">Counts as a reply on this RFP and stops anything still planned for this company. Don't log the same reply twice.</p>}
      <Button variant="primary" size="sm" disabled={busy || !text.trim()} onClick={add}>Add to thread</Button>
    </div>
  );
}

function ResultDialog({ outcome, who, company, busy, initialNote, onCancel, onConfirm }: {
  outcome: ConversationOutcome; who: string; company: string | null; busy: boolean; initialNote: string; onCancel: () => void; onConfirm: (note: string) => void;
}) {
  const [note, setNote] = useState(initialNote);
  const help = RESULT_HELP[outcome];
  return (
    <Modal
      size="md" title={`Set the result: ${OUTCOME_LABEL[outcome]}`} onClick={onCancel} closeButtonCLick={onCancel}
      footer={<>
        <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="primary" disabled={busy} onClick={() => onConfirm(note.trim())}>{busy ? "Saving…" : `Set ${OUTCOME_LABEL[outcome]}`}</Button>
      </>}
    >
      <div className="space-y-3 text-sm">
        {help && <p>{fill(help.lead, who, company)}</p>}
        {help && <p><strong>{fill(help.effect, who, company)}</strong></p>}
        <div>
          <label htmlFor={`result-note-${outcome}`} className="block text-xs font-medium text-muted-foreground mb-1.5">Note (optional)</label>
          <textarea id={`result-note-${outcome}`} value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} className={`w-full px-3 py-2 ${FORM_CONTROL}`} />
        </div>
      </div>
    </Modal>
  );
}

function RemoveDialog({ who, busy, onCancel, onConfirm }: { who: string; busy: boolean; onCancel: () => void; onConfirm: (reason: string) => void }) {
  const [reason, setReason] = useState("");
  return (
    <Modal
      size="md" title="They asked to be removed" onClick={onCancel} closeButtonCLick={onCancel}
      footer={<>
        <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
        <Button variant="danger" disabled={busy || !reason.trim()} onClick={() => onConfirm(reason.trim())}>{busy ? "Saving…" : "Mark Do Not Contact"}</Button>
      </>}
    >
      <div className="space-y-3 text-sm">
        <p>{who} asked to be taken off our list. The conversation closes as <strong>asked to be removed</strong> and <strong>{who}</strong> is marked <strong>Do Not Contact</strong>: nothing further is sent to them, on any RFP. Colleagues at the same company are not affected.</p>
        <div>
          <label htmlFor="dnc-reason" className="block text-xs font-medium text-muted-foreground mb-1.5">Reason (required)</label>
          <textarea id="dnc-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} className={`w-full px-3 py-2 ${FORM_CONTROL}`} />
        </div>
      </div>
    </Modal>
  );
}

function RedirectDialog({ conversation, busy, onCancel, onPick }: { conversation: Conversation; busy: boolean; onCancel: () => void; onPick: (id: number) => void }) {
  const [people, setPeople] = useState<PrimeContactWithAppearanceCount[] | null>(null);
  const [picked, setPicked] = useState<number | "">("");
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    getPrimeCompany(conversation.prime_company_id)
      .then((co) => { if (live) setPeople((co.prime_contacts ?? []).filter((p) => p.id !== conversation.prime_contact_id && p.status === "active" && (p.first_name ?? "").trim())); })
      .catch((e) => { if (live) { setPeople([]); setError(apiErrorMessage(e, "Could not load the company's contacts.")); } });
    return () => { live = false; };
  }, [conversation.prime_company_id, conversation.prime_contact_id]);
  return (
    <>
      <Modal
        size="md" title="They pointed us to a colleague" onClick={onCancel} closeButtonCLick={onCancel}
        footer={<>
          <Button variant="outline" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button variant="primary" disabled={busy || picked === ""} onClick={() => picked !== "" && onPick(picked)}>{busy ? "Saving…" : "Start conversation"}</Button>
        </>}
      >
        <div className="space-y-3 text-sm">
          <p>This conversation closes as redirected, and a new one starts with the colleague. Nothing is sent to them automatically.</p>
          {error && <Alert tone="danger" role="alert">{error}</Alert>}
          <label htmlFor="redirect-to" className="block text-xs font-medium text-muted-foreground">Colleague at {conversation.company_name}</label>
          <select id="redirect-to" value={picked} onChange={(e) => setPicked(e.target.value ? Number(e.target.value) : "")} className={`w-full px-3 py-2 ${FORM_CONTROL}`}>
            <option value="">{people === null ? "Loading…" : "Choose a contact"}</option>
            {(people ?? []).map((p) => <option key={p.id} value={p.id}>{[p.first_name, p.last_name].filter(Boolean).join(" ")} ({p.primary_email})</option>)}
          </select>
          <button type="button" className="text-sm font-medium text-primary underline underline-offset-2" onClick={() => setAdding(true)}>Add a new contact</button>
        </div>
      </Modal>
      {adding && (
        <AddPrimeContactModal
          company={{ id: conversation.prime_company_id, name: conversation.company_name }}
          onClose={() => setAdding(false)}
          onSaved={(contact) => { setAdding(false); setPeople((prev) => [...(prev ?? []), contact as PrimeContactWithAppearanceCount]); setPicked(contact.id); }}
        />
      )}
    </>
  );
}
